import { useEffect } from "react";
import LoadingBar from "@components/ui/LoadingBar";
import Icon1 from "@assets/icons/Icon";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useDashboardStatsStore } from "@store/dashboardStatsStore";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "context/navigation.service";

function StatCard({ label, value, icon, color, loading, onClick }: {
  label: string;
  value: number;
  icon: string;
  color: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-1 bg-white border rounded-xl p-4 flex-1 min-w-140 shadow-lg relative overflow-hidden cursor-pointer hover:border-neutral-300 transition-colors"
      onClick={onClick}
    >
      {loading && <LoadingBar variant="absolute" />}
      <div className="flex flex-row items-center justify-between">
        <div className="text-h3 text-neutral-400">{label}</div>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: color + "15" }}
        >
          <Icon1 name={icon} size={18} color={color} />
        </div>
      </div>
      <div className="text-2xl font-semibold text-black">{value}</div>
    </div>
  );
}

export default function StatCardsWidget() {
  const orgData = useDataStore((s) => s.orgData);
  const dashStats = useDashboardStatsStore((s) => s.stats);
  const isRefreshing = useDashboardStatsStore((s) => s.isRefreshing);
  const fetchStats = useDashboardStatsStore((s) => s.fetchStats);
  const vehicles = useVehiculesStore((s) => s.vehicles);
  const vehiclesRefreshing = useVehiculesStore((s) => s.isRefreshing);
  const loading = isRefreshing || vehiclesRefreshing;

  useEffect(() => {
    if (orgData?.id) fetchStats(orgData.id);
  }, [orgData?.id]);

  const computedAt = dashStats?.computed_at ? new Date(dashStats.computed_at) : null;
  const minutesAgo = computedAt ? Math.round((Date.now() - computedAt.getTime()) / 60000) : null;

  const stats = [
    { label: "Ordres", value: dashStats?.total ?? 0, icon: "document", color: "#6366F1", route: "/ordres" },
    { label: "Prépa.", value: dashStats?.prepa ?? 0, icon: "restart", color: "#6366F1", route: "/ordres?status=a_preparer" },
    { label: "En cours", value: dashStats?.en_cours ?? 0, icon: "restart", color: "#0EA5E9", route: "/ordres?status=en_cours" },
    { label: "Terminés", value: dashStats?.termines ?? 0, icon: "done2", color: "#22C55E", route: "/ordres?status=restitue" },
    { label: "Bloqués", value: dashStats?.bloques ?? 0, icon: "warning", color: "#DC2626", route: "/ordres?flag=bloque" },
    { label: "Urgents", value: dashStats?.urgents ?? 0, icon: "flag", color: "#D97706", route: "/ordres?flag=urgent" },
    { label: "Véhicules", value: vehicles.length, icon: "car", color: "#6366F1", route: "/vehicules" },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-3 flex-wrap">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} loading={loading} onClick={() => navigateTo({ route: stat.route })} />
        ))}
      </div>
      {dashStats && (
        <div className="flex flex-row items-center gap-2 text-xs text-neutral-400 px-1">
          {minutesAgo !== null && (
            <span>
              Mis à jour il y a{" "}
              {minutesAgo < 1
                ? "moins d'une minute"
                : minutesAgo < 60
                  ? `${minutesAgo} min`
                  : `${Math.round(minutesAgo / 60)}h`}
            </span>
          )}
          <button
            onClick={() => orgData?.id && fetchStats(orgData.id, true)}
            disabled={isRefreshing}
            className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 disabled:text-neutral-300"
          >
            <Icon1 name="restart" size={12} color={isRefreshing ? "#D4D4D4" : "#6366F1"} />
            {isRefreshing ? "Actualisation..." : "Actualiser"}
          </button>
        </div>
      )}
    </div>
  );
}
